import { Product, Theme, UserDiscount } from 'referral-types';
import { getCollectionId, getPriceWithDiscount } from './utils';
import { getColorHexs } from '../theme/constants';
import { StoreThemeContextType } from '../../context/store-theme/StoreThemeContext';

export const DEFAULT_USER_ID = '3f7c3e00-487e-4866-8d5a-b74c4b3ce16f';
export const DEFAULT_REFERRAL_ID = '8b9b5451-4298-4dc8-9a2b-e3ade808ecc1';
export const DEFAULT_PAGE_ID = '5a5cb0e1-7d03-4471-ad6f-c93342e2c9d2';

export enum ProductType {
  color = 'color',
  background = 'background',
  header = 'header',
  group = 'group',
  referral = 'referral',
  feature = 'feature',
  tip = 'tip',
}

export function getProductLabel(productType: ProductType): string {
  switch (productType) {
    case ProductType.background:
      return 'Background Themes';
    case ProductType.color:
      return 'Page Themes';
    case ProductType.group:
      return 'Group Themes';
    case ProductType.referral:
      return 'Referral Themes';
    // case ProductType.navbar:
    //     return 'Navbar Themes';
    case ProductType.header:
      return 'Header Themes';
    case ProductType.feature:
      return 'Site Features';
    case ProductType.tip:
      return 'Tips';
  }
}

export const ProductCategories: Array<{ value: string; label: string }> = [
  { value: ProductType.background, label: getProductLabel(ProductType.background) },
  // { value: ProductType.color, label: getProductLabel(ProductType.color) },
  { value: ProductType.group, label: getProductLabel(ProductType.group) },
  { value: ProductType.referral, label: getProductLabel(ProductType.referral) },
  // { value: ProductType.navbar, label: getProductLabel(ProductType.navbar) },
  { value: ProductType.header, label: getProductLabel(ProductType.header) },
];

export function getProductPriceTotal(products: Array<Product>, userDiscounts: Array<UserDiscount>): number {
  if (products.length === 0) {
    return 0;
  }
  const price = products
    .map((o) =>
      getPriceWithDiscount(
        o.price,
        userDiscounts.find((d) => d.discountCollectionId === getCollectionId(o))
      )
    )
    .reduce((a, b) => Number(a) + Number(b));

  return price;
}

export function getProductPriceLabel(products: Array<Product>, userDiscounts: Array<UserDiscount>) {
  const priceTotal = getProductPriceTotal(products, userDiscounts);

  return priceTotal === 0 ? 'FREE' : '$' + priceTotal.toFixed(2);
}

export const StoreTopAnchorId = 'store-top_anchor';

export function getProductTitle(product: Product, theme: Theme): string {
  const { collectionTitle } = theme;

  if (collectionTitle) {
    return `${collectionTitle} [${product.title}]`;
  } else {
    return product.title;
  }
}
export function removeLastWord(title: string) {
  const words = title.trim().split(' ');

  words.pop();

  return words.join(' ');
}

export function getStripeAppearance(colorName: string, isDarkMode?: boolean) {
  const hexs = getColorHexs({ colorScheme: colorName.toLowerCase(), isDarkMode, primaryColorName: colorName });

  return isDarkMode
    ? {
        variables: {
          colorPrimary: hexs.base,
          colorBackground: '#111827', // dark background
          colorText: '#ffffff', // white text
          colorTextSecondary: '#b3b3b3', // slightly dimmed secondary text
          colorDanger: '#df1b41', // bright red for errors
        },
        rules: {
          '.Input': {
            color: '#ffffff', // Input text color
          },
          '.Label': {
            color: '#b3b3b3', // Label color
          },
          '.Tab': {
            backgroundColor: '#333333', // Tab background color
            color: '#ffffff', // Tab text color
          },
        },
      }
    : {
        variables: {
          colorPrimary: hexs.base,
          colorDanger: '#df1b41',
        },
      };
}

export function getStoreFooterProps(
  selectedThemeTab: ProductType,
  storeContext: StoreThemeContextType
): StoreFooterReturn {
  const {
    selectedBackgroundTheme,
    setSelectedBackgroundTheme,
    selectedPageTheme,
    setSelectedPageTheme,
    selectedGroupTheme,
    setSelectedGroupTheme,
    selectedHeaderTheme,
    setSelectedHeaderTheme,
    // selectedNavbarTheme,
    // setSelectedNavbarTheme,
    selectedReferralTheme,
    setSelectedReferralTheme,
    availableProducts,
  } = storeContext;

  let currentProduct: Product | undefined;
  let sortedCollectionProducts: Array<Product>;
  let setProduct: (product: Product) => void;

  switch (selectedThemeTab) {
    case ProductType.background:
      setProduct = (p: Product) => setSelectedBackgroundTheme(p.backgroundTheme!);
      currentProduct = availableProducts?.backgroundProducts.find(
        (o) => o.backgroundTheme?.id === selectedBackgroundTheme?.id
      );
      sortedCollectionProducts =
        currentProduct?.backgroundTheme?.collectionId == null
          ? currentProduct
            ? [currentProduct]
            : []
          : availableProducts!.backgroundProducts
              .filter((o) => o.backgroundTheme?.collectionId === currentProduct!.backgroundTheme!.collectionId!)
              .sort((a, b) => a.backgroundTheme!.collectionTier! - b.backgroundTheme!.collectionTier!);
      break;
    case ProductType.color:
      setProduct = (p: Product) => setSelectedPageTheme(p.pageTheme!);
      currentProduct = availableProducts?.colorProducts.find((o) => o.pageTheme!.id === selectedPageTheme!.id);
      sortedCollectionProducts =
        currentProduct?.pageTheme?.collectionId == null
          ? currentProduct
            ? [currentProduct]
            : []
          : availableProducts!.colorProducts
              .filter((o) => o.pageTheme?.collectionId === currentProduct?.pageTheme?.collectionId)
              .sort((a, b) => a.pageTheme!.collectionTier! - b.pageTheme!.collectionTier!);
      break;
    case ProductType.group:
      setProduct = (p: Product) => setSelectedGroupTheme(p.groupTheme!);
      currentProduct = availableProducts?.groupProducts.find((o) => o.groupTheme!.id === selectedGroupTheme!.id);
      sortedCollectionProducts =
        currentProduct?.groupTheme?.collectionId == null
          ? currentProduct
            ? [currentProduct]
            : []
          : availableProducts!.groupProducts
              .filter((o) => o.groupTheme?.collectionId === currentProduct?.groupTheme?.collectionId)
              .sort((a, b) => a.groupTheme!.collectionTier! - b.groupTheme!.collectionTier!);
      break;
    case ProductType.referral:
      setProduct = (p: Product) => setSelectedReferralTheme(p.referralTheme!);
      currentProduct = availableProducts?.referralProducts.find(
        (o) => o.referralTheme!.id === selectedReferralTheme!.id
      );
      sortedCollectionProducts =
        currentProduct?.referralTheme?.collectionId == null
          ? currentProduct
            ? [currentProduct]
            : []
          : availableProducts!.referralProducts
              .filter((o) => o.referralTheme?.collectionId === currentProduct!.referralTheme!.collectionId)
              .sort((a, b) => a.referralTheme!.collectionTier! - b.referralTheme!.collectionTier!);
      break;
    case ProductType.header:
    default:
      setProduct = (p: Product) => setSelectedHeaderTheme(p.headerTheme!);
      currentProduct = availableProducts?.headerProducts.find((o) => o.headerTheme?.id === selectedHeaderTheme?.id);
      sortedCollectionProducts =
        currentProduct?.headerTheme?.collectionId == null
          ? currentProduct
            ? [currentProduct]
            : []
          : availableProducts!.headerProducts
              .filter((o) => o.headerTheme?.collectionId === currentProduct!.headerTheme!.collectionId)
              .sort((a, b) => a.headerTheme!.collectionTier! - b.headerTheme!.collectionTier!);
      break;
    // case ProductType.navbar:
    // default:
    //     setProduct = (p: Product) => setSelectedNavbarTheme(p.navbarTheme!);
    //     currentProduct = availableProducts?.navbarProducts.find(o => o.navbarTheme?.id === selectedNavbarTheme?.id);
    //     sortedCollectionProducts = currentProduct?.navbarTheme?.collectionId == null
    //         ? currentProduct ? [currentProduct] : []
    //         : availableProducts!.navbarProducts
    //             .filter(o => o.navbarTheme?.collectionId === currentProduct!.navbarTheme!.collectionId)
    //             .sort((a, b) => a.navbarTheme!.collectionTier! - b.navbarTheme!.collectionTier!);
  }

  return {
    currentProduct,
    sortedCollectionProducts,
    setProduct,
  };
}

type StoreFooterReturn = {
  currentProduct: Product | undefined;
  sortedCollectionProducts: Array<Product>;
  setProduct: (product: Product) => void;
};
